import React, { useState, useRef, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaSpinner } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ProductSection = ({ title, products, isLoading: parentLoading }) => {
  const scrollRef = useRef(null);
  const [cartCount, setCartCount] = useState(0);
 
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const navigate = useNavigate();
  const [forceUpdateKey, setForceUpdateKey] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Use parent loading state or local check if products are empty
  const isLoading = parentLoading || !products || products.length === 0;

  useEffect(() => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    setCartCount(storedCartItems.reduce((acc, item) => acc + item.quantity, 0));
    checkScrollPosition();
  }, []);


  useEffect(() => {
    const handleResize = () => {
      checkScrollPosition();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const updateCartCount = () => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    setCartCount(storedCartItems.reduce((acc, item) => acc + item.quantity, 0));
  };

  const forceUpdateProducts = () => {
    setForceUpdateKey(prevKey => prevKey + 1);
  };

  const checkScrollPosition = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      const newScrollLeft = direction === 'left' 
        ? Math.max(0, scrollRef.current.scrollLeft - scrollAmount)
        : scrollRef.current.scrollLeft + scrollAmount;
      
      scrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
      
      setTimeout(checkScrollPosition, 500);
    }
  };

  // Touch/swipe handling
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    scrollRef.current.style.cursor = 'grab';
    scrollRef.current.style.removeProperty('user-select');
    checkScrollPosition();
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.removeProperty('user-select');
    }
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    checkScrollPosition();
  };

  const handleScrollEvent = () => {
    checkScrollPosition();
  };

  if (isLoading) {
    return (
      <div className=" px-4">
        <h2 className="text-3xl font-bold text-center mb-6">{title}</h2>
        <div className="flex overflow-x-hidden space-x-6 py-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="flex-shrink-0 w-64 h-88 bg-white rounded-xl shadow-md p-4 animate-pulse">
              <div className="w-full h-64 bg-gray-200 rounded-lg"></div>
              <div className="mt-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                <div className="h-12 bg-gray-200 rounded mt-3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <section className="my-12 relative px-4">
      <h2 className="text-3xl font-bold text-center mb-8">{title}</h2>
      
      {/* Left Arrow */}
      <button
        className={`hidden md:flex items-center justify-center absolute left-2 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ${
          showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => handleScroll('left')}
        aria-label="Scroll left"
      >
        <FaChevronLeft className="text-[#007D44] text-xl" />
      </button>
      
      {/* Right Arrow */}
      <button
        className={`hidden md:flex items-center justify-center absolute right-2 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ${
          showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => handleScroll('right')}
        aria-label="Scroll right"
      >
        <FaChevronRight className="text-[#007D44] text-xl" />
      </button>
      
      {/* Scrollable Container */}
      <div
        className="overflow-x-auto overflow-y-hidden whitespace-nowrap py-4 cursor-grab active:cursor-grabbing"
        ref={scrollRef}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onScroll={handleScrollEvent}
      >
        <style>
          {`
            .scroll-container::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        <div className="inline-flex space-x-6 px-2">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              updateCartCount={updateCartCount}
              forceUpdateProducts={forceUpdateKey}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ProductCard = ({ product, updateCartCount, forceUpdateProducts,  }) => {
  const [inCart, setInCart] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    const foundItem = storedCartItems.find(item => item.id === product.id);
    if (foundItem) {
      setInCart(true);
      setQuantity(foundItem.quantity);
    }

    const img = new Image();
    img.src = product.img;
    img.onload = () => setImageLoading(false);
    img.onerror = () => setImageLoading(false);
  }, [product.id, product.img, forceUpdateProducts]);

  const handleProductClick = () => {
    if (product.quantity > 0) {
      navigate(`/product/${product.name}`);
    }
  };

  const handleAddToCart = () => {
    if (!inCart) {
      setInCart(true);
      const newQuantity = 1;
      setQuantity(newQuantity);
      const updatedCart = saveToLocalStorage(product, newQuantity);
      updateCartCount(updatedCart);
    }
  };

  const increaseQuantity = () => {
    setQuantity(prevQuantity => {
      const newQuantity = prevQuantity + 1;
      const updatedCart = updateLocalStorage(product.id, newQuantity);
      updateCartCount(updatedCart);
      return newQuantity;
    });
  };

  const decreaseQuantity = () => {
    setQuantity(prevQuantity => {
      const newQuantity = prevQuantity - 1;
      let updatedCart;
      if (newQuantity === 0) {
        setInCart(false);
        updatedCart = removeFromLocalStorage(product.id);
      } else {
        updatedCart = updateLocalStorage(product.id, newQuantity);
      }
      updateCartCount(updatedCart);
      return newQuantity;
    });
  };

  const saveToLocalStorage = (product, quantity) => {
    const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    const updatedCart = [...cartItems, { ...product, quantity }];
    localStorage.setItem('cartItems', JSON.stringify(updatedCart));
    return updatedCart;
  };

  const updateLocalStorage = (productId, newQuantity) => {
    const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    const updatedCart = cartItems.map(item =>
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    localStorage.setItem('cartItems', JSON.stringify(updatedCart));
    return updatedCart;
  };

  const removeFromLocalStorage = (productId) => {
    const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    const updatedCart = cartItems.filter(item => item.id !== productId);
    localStorage.setItem('cartItems', JSON.stringify(updatedCart));
    return updatedCart;
  };

  return (
    <div className="inline-block w-64 h-88 bg-white shadow-lg rounded-xl overflow-hidden hover:shadow-xl transition-shadow duration-300 flex-shrink-0">
      <div 
        onClick={handleProductClick}  
        className="relative cursor-pointer h-64 bg-gray-50 flex items-center justify-center"
      >
        {imageLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <FaSpinner className="animate-spin text-gray-300 text-2xl" />
          </div>
        ) : (
          <>
            <img 
              src={product.img} 
              alt={product.name} 
              className="w-full h-full object-contain p-4"
              loading="lazy"
            />
            {product.quantity === 0 && (
              <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                <span className="text-white text-lg font-bold uppercase tracking-wide">
                  Out of Stock
                </span>
              </div>
            )}
          </>
        )}
      </div>
      
      <div className="p-4 space-y-3">
        <h3 className="font-semibold text-gray-800 text-center line-clamp-2 h-14">
          {product.name}
        </h3>
        <p className="text-[#007D44] font-bold text-xl text-center">
          ${product.price}
        </p>

        {inCart ? (
          <div className="flex items-center justify-between bg-[#007D44]/10 rounded-lg px-4 py-2">
            <button 
              onClick={decreaseQuantity}
              className="text-[#007D44] hover:bg-[#007D44]/20 p-2 rounded-full transition-colors"
            >
              -
            </button>
            <span className="font-bold text-[#007D44]">{quantity}</span>
            <button 
              onClick={increaseQuantity}
              className="text-[#007D44] hover:bg-[#007D44]/20 p-2 rounded-full transition-colors"
            >
              +
            </button>
          </div>
        ) : (
          <button
            onClick={handleAddToCart}
            disabled={product.quantity === 0}
            className={`w-full py-3 rounded-lg font-semibold transition-colors ${
              product.quantity === 0 
                ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
                : 'bg-[#007D44] hover:bg-[#006633] text-white'
            }`}
          >
            Add to Cart
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductSection;